import { useEffect } from "react";
import { Phone, WhatsappLogo, ShieldCheck } from "@phosphor-icons/react";

const PHONE = "9742119056";
const WA = `https://wa.me/91${PHONE}`;
const BUSINESS = "Home Tech Repair";
const DOMAIN = "hometechreapair.com";
const EMAIL = "sharukh280@gmail.com";

export default function PrivacyPolicyPage() {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div className="min-h-screen bg-[#F8FAFC] font-['Poppins']">
      {/* Header */}
      <header className="bg-white border-b border-slate-100 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 md:px-8 flex items-center justify-between h-16">
          <a href="/" className="font-bold text-[#0F172A] text-base uppercase tracking-tight">{BUSINESS}</a>
          <a href={`tel:${PHONE}`} className="bg-[#FF6B00] text-white hover:bg-[#E55F00] font-bold text-sm px-5 py-2.5 rounded-full transition-all shadow-lg shadow-orange-200 flex items-center gap-1.5">
            <Phone size={16} weight="fill" /> Call Now
          </a>
        </div>
      </header>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 md:px-8 py-12 md:py-16">
        <div className="flex items-center gap-3 mb-8">
          <ShieldCheck size={32} weight="duotone" className="text-[#FF6B00]" />
          <div>
            <h1 className="text-3xl sm:text-4xl font-extrabold text-[#0F172A] tracking-tight">Privacy Policy</h1>
            <p className="text-sm text-slate-400 mt-1">Last updated: July 2025</p>
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6 sm:p-10 space-y-8 text-slate-600 text-sm leading-relaxed">

          <section>
            <h2 className="text-lg font-bold text-[#0F172A] mb-3">1. Introduction</h2>
            <p>
              {BUSINESS} ("we," "us," or "our") operates the website <strong>{DOMAIN}</strong> and provides home appliance repair and maintenance services in Bangalore, India. This Privacy Policy describes how we collect, use, store, and protect your personal information when you visit our website, fill out our booking forms, or contact us via phone or WhatsApp.
            </p>
            <p className="mt-2">
              By using our website or submitting your information through our forms, you agree to the collection and use of information in accordance with this policy.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-bold text-[#0F172A] mb-3">2. Information We Collect</h2>
            <p>We collect the following personal information when you submit a booking or enquiry form on our website:</p>
            <ul className="list-disc list-inside mt-2 space-y-1.5 ml-2">
              <li><strong>Name</strong> — to identify you and address you personally</li>
              <li><strong>Phone Number</strong> — to call you back and confirm your service booking</li>
              <li><strong>Area/Location</strong> — to assign the nearest available technician</li>
              <li><strong>Service Type / Problem Description</strong> — to understand your requirement and prepare accordingly</li>
            </ul>
            <p className="mt-3">We do <strong>not</strong> collect:</p>
            <ul className="list-disc list-inside mt-1 space-y-1 ml-2 text-slate-500">
              <li>Payment or credit card information</li>
              <li>Passwords or login credentials</li>
              <li>Aadhaar, PAN, or other government ID numbers</li>
              <li>Precise GPS location data</li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-bold text-[#0F172A] mb-3">3. How We Use Your Information</h2>
            <p>Your personal information is used solely for the following purposes:</p>
            <ul className="list-disc list-inside mt-2 space-y-1.5 ml-2">
              <li>To contact you and confirm your service booking</li>
              <li>To assign a technician to your location</li>
              <li>To send you service updates via phone call, SMS, or WhatsApp</li>
              <li>To improve our services and customer experience</li>
              <li>To respond to your enquiries and provide customer support</li>
            </ul>
            <p className="mt-3">
              We do <strong>not</strong> use your information for automated decision-making, profiling, or any purpose other than providing the service you requested.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-bold text-[#0F172A] mb-3">4. Data Storage & Security</h2>
            <p>
              Your data is stored securely in our database hosted on cloud infrastructure. We implement appropriate technical and organizational measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction.
            </p>
            <ul className="list-disc list-inside mt-2 space-y-1.5 ml-2">
              <li>Data is transmitted over encrypted HTTPS connections</li>
              <li>Access to customer data is restricted to authorized personnel only</li>
              <li>We do not store any payment or financial information</li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-bold text-[#0F172A] mb-3">5. Data Sharing & Disclosure</h2>
            <p>We do <strong>not</strong> sell, rent, or trade your personal information to third parties. Your information may be shared only in the following limited circumstances:</p>
            <ul className="list-disc list-inside mt-2 space-y-1.5 ml-2">
              <li><strong>Service Technicians</strong> — Your name, phone number, and area are shared with our technicians solely to provide the requested service at your location</li>
              <li><strong>Email Notifications</strong> — We use a third-party email service (Resend) to send internal lead notifications to our team. Your data is not shared with Resend for marketing purposes</li>
              <li><strong>Legal Requirements</strong> — We may disclose your information if required by law, court order, or government regulation</li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-bold text-[#0F172A] mb-3">6. Cookies & Tracking</h2>
            <p>
              Our website may use cookies and similar tracking technologies (such as Google Analytics and Google Tag Manager) to understand website traffic and improve user experience. These tools may collect:
            </p>
            <ul className="list-disc list-inside mt-2 space-y-1.5 ml-2">
              <li>Browser type and version</li>
              <li>Pages visited and time spent</li>
              <li>Referring website or ad source</li>
              <li>Device type (mobile/desktop)</li>
            </ul>
            <p className="mt-3">
              This data is aggregated and anonymous. You can disable cookies in your browser settings at any time.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-bold text-[#0F172A] mb-3">7. Google Ads & Remarketing</h2>
            <p>
              We use Google Ads to advertise our services. Google may use cookies to serve ads based on your prior visits to our website. This is known as remarketing. You can opt out of personalized advertising by visiting{" "}
              <a href="https://adssettings.google.com" target="_blank" rel="noopener noreferrer" className="text-[#FF6B00] underline">Google Ads Settings</a>.
            </p>
            <p className="mt-2">
              We comply with Google's advertising policies and do not collect sensitive information categories for advertising purposes.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-bold text-[#0F172A] mb-3">8. Your Rights</h2>
            <p>You have the following rights regarding your personal information:</p>
            <ul className="list-disc list-inside mt-2 space-y-1.5 ml-2">
              <li><strong>Right to Access</strong> — You can request a copy of the personal data we hold about you</li>
              <li><strong>Right to Correction</strong> — You can ask us to correct inaccurate information</li>
              <li><strong>Right to Deletion</strong> — You can request that we delete your personal data from our records</li>
              <li><strong>Right to Withdraw Consent</strong> — You can withdraw your consent for us to process your data at any time</li>
            </ul>
            <p className="mt-3">
              To exercise any of these rights, please contact us using the details provided below.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-bold text-[#0F172A] mb-3">9. Data Retention</h2>
            <p>
              We retain your personal information only for as long as necessary to fulfill the purposes outlined in this policy, or as required by applicable law. Typically, lead data is retained for up to 12 months after your last interaction with us, after which it may be deleted.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-bold text-[#0F172A] mb-3">10. Third-Party Links</h2>
            <p>
              Our website may contain links to third-party websites (e.g., WhatsApp, Google). We are not responsible for the privacy practices of these external sites. We recommend reviewing their privacy policies separately.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-bold text-[#0F172A] mb-3">11. Children's Privacy</h2>
            <p>
              Our services are not directed at individuals under the age of 18. We do not knowingly collect personal information from children. If you believe a child has submitted information through our forms, please contact us and we will promptly delete the data.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-bold text-[#0F172A] mb-3">12. Changes to This Policy</h2>
            <p>
              We may update this Privacy Policy from time to time. Any changes will be posted on this page with an updated "Last updated" date. We encourage you to review this page periodically.
            </p>
          </section>

          <section className="bg-[#F8FAFC] rounded-xl p-6 border border-slate-100">
            <h2 className="text-lg font-bold text-[#0F172A] mb-3">13. Contact Us</h2>
            <p>If you have any questions about this Privacy Policy or wish to exercise your data rights, please contact us:</p>
            <div className="mt-4 space-y-2">
              <p><strong>Business Name:</strong> {BUSINESS}</p>
              <p><strong>Location:</strong> Bangalore, Karnataka, India</p>
              <p><strong>Phone:</strong> <a href={`tel:${PHONE}`} className="text-[#FF6B00] font-semibold">{PHONE}</a></p>
              <p><strong>Email:</strong> <a href={`mailto:${EMAIL}`} className="text-[#FF6B00] font-semibold">{EMAIL}</a></p>
              <p><strong>WhatsApp:</strong> <a href={WA} target="_blank" rel="noopener noreferrer" className="text-[#22C55E] font-semibold">Chat on WhatsApp</a></p>
              <p><strong>Website:</strong> <a href={`https://${DOMAIN}`} className="text-[#FF6B00] font-semibold">{DOMAIN}</a></p>
            </div>
          </section>

        </div>
      </div>

      {/* Footer */}
      <footer className="bg-[#0F172A] py-8">
        <div className="max-w-4xl mx-auto px-4 md:px-8 flex flex-col md:flex-row items-center justify-between gap-4">
          <div>
            <span className="font-bold text-white text-sm block uppercase tracking-tight">{BUSINESS}</span>
            <span className="text-white/30 text-xs">Home Appliance Repair — Bangalore</span>
          </div>
          <div className="flex items-center gap-5 text-sm text-white/40">
            <a href={`tel:${PHONE}`} className="hover:text-white transition-colors flex items-center gap-1.5"><Phone size={14} weight="fill" /> {PHONE}</a>
            <a href={WA} target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors flex items-center gap-1.5"><WhatsappLogo size={14} weight="fill" /> WhatsApp</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
